import {
  type ApplyIntakeChartEntriesRequest,
  ApplyIntakeChartEntriesRequestSchema,
  type ApplyIntakeChartEntriesResponse,
  ApplyIntakeChartEntriesResponseSchema,
  type ApplyIntakeDemographicsRequest,
  ApplyIntakeDemographicsRequestSchema,
  type ApplyIntakeDemographicsResponse,
  ApplyIntakeDemographicsResponseSchema,
  type ApplyIntakeReasonRequest,
  ApplyIntakeReasonRequestSchema,
  type ApplyIntakeReasonResponse,
  ApplyIntakeReasonResponseSchema,
  type MarkIntakeSectionReviewedRequest,
  MarkIntakeSectionReviewedRequestSchema,
  type MarkIntakeSectionReviewedResponse,
  MarkIntakeSectionReviewedResponseSchema,
  type ReconcileSchedulingPatientRequest,
  ReconcileSchedulingPatientRequestSchema,
  type ReconcileSchedulingPatientResponse,
  ReconcileSchedulingPatientResponseSchema,
  type SchedulingIntakeReview,
  type SchedulingIntakeReviewList,
  type SchedulingIntakeReviewListFilter,
  SchedulingIntakeReviewListFilterSchema,
  SchedulingIntakeReviewListSchema,
  SchedulingIntakeReviewSchema,
  type SchedulingPatientMatches,
  SchedulingPatientMatchesSchema,
  type WalkInChartMatchesRequest,
  WalkInChartMatchesRequestSchema,
} from "@emr/contracts";
import { z } from "zod";
import { SchedulingRequestError, schedulingRequestError } from "../demo/schedulingErrors";

/**
 * Pre-visit questionnaire review and apply (PA-6 / EXECDEV-214) on the staff scheduling lane.
 * Every route acts on the client's own site (its `x-site-id`), so none takes a site argument.
 * Requests are checked against the contract before they leave and responses after they arrive;
 * errors are SchedulingRequestError, whose `detail` names a closed review
 * (`intake_review_closed`), changed chart matches (`chart_matches_changed`), a changed chart
 * (`chart_changed`) and the rest of SchedulingErrorDetail.
 *
 * Reads are audited on the server, so callers fetch them when a review or list opens, never on
 * the schedule poll:
 *
 * - GET  /v1/scheduling/intake-reviews?filter=              questionnaires still to review
 * - GET  /v1/scheduling/appointments/:id/intake             the review (answers by role)
 * - GET  /v1/scheduling/appointments/:id/patient-matches    probable charts
 * - POST /v1/scheduling/appointments/:id/patient            link, create or mismatch
 * - POST /v1/scheduling/appointments/:id/intake/demographics
 * - POST /v1/scheduling/appointments/:id/intake/chart-entries
 * - POST /v1/scheduling/appointments/:id/intake/reason
 * - POST /v1/scheduling/appointments/:id/intake/reviewed
 * - POST /v1/scheduling/walk-ins/chart-matches            probable charts for a walk-in (PA-5)
 */
export function createSchedulingIntakeClient(
  request: (path: string, body?: unknown, signal?: AbortSignal) => Promise<Response>,
) {
  async function json<T extends z.ZodType>(
    path: string,
    schema: T,
    fallbackMessage: string,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<z.output<T>> {
    const response = await request(path, body, signal);
    if (!response.ok) throw await schedulingRequestError(response, fallbackMessage);
    return schema.parse(await response.json());
  }
  /** Durable appointment ids are uuids; anything else never reaches a URL. */
  const appointmentPath = (appointmentId: string, suffix: string) =>
    `/v1/scheduling/appointments/${encodeURIComponent(z.string().uuid().parse(appointmentId))}${suffix}`;

  return {
    /** Questionnaires with a section the signed-in member still reviews, across every date. */
    async listSchedulingIntakeReviews(
      filter: SchedulingIntakeReviewListFilter = "to_review",
      signal?: AbortSignal,
    ): Promise<SchedulingIntakeReviewList> {
      const query = new URLSearchParams({
        filter: SchedulingIntakeReviewListFilterSchema.parse(filter),
      });
      return json(
        `/v1/scheduling/intake-reviews?${query}`,
        SchedulingIntakeReviewListSchema,
        "The questionnaires to review could not be loaded.",
        undefined,
        signal,
      );
    },

    /** The submitted answers, split by section for the signed-in role, with what was applied. */
    async getSchedulingIntakeReview(
      appointmentId: string,
      signal?: AbortSignal,
    ): Promise<SchedulingIntakeReview> {
      return json(
        appointmentPath(appointmentId, "/intake"),
        SchedulingIntakeReviewSchema,
        "The questionnaire could not be loaded.",
        undefined,
        signal,
      );
    },

    async getSchedulingPatientMatches(
      appointmentId: string,
      signal?: AbortSignal,
    ): Promise<SchedulingPatientMatches> {
      return json(
        appointmentPath(appointmentId, "/patient-matches"),
        SchedulingPatientMatchesSchema,
        "The possible chart matches could not be loaded.",
        undefined,
        signal,
      );
    },

    /** Links an existing chart, creates one from the answers, or records "none of these". */
    async reconcileSchedulingPatient(
      appointmentId: string,
      input: ReconcileSchedulingPatientRequest,
      signal?: AbortSignal,
    ): Promise<ReconcileSchedulingPatientResponse> {
      return json(
        appointmentPath(appointmentId, "/patient"),
        ReconcileSchedulingPatientResponseSchema,
        "The chart could not be updated for this booking.",
        ReconcileSchedulingPatientRequestSchema.parse(input),
        signal,
      );
    },

    async applySchedulingIntakeDemographics(
      appointmentId: string,
      input: ApplyIntakeDemographicsRequest,
      signal?: AbortSignal,
    ): Promise<ApplyIntakeDemographicsResponse> {
      return json(
        appointmentPath(appointmentId, "/intake/demographics"),
        ApplyIntakeDemographicsResponseSchema,
        "The patient's details could not be applied to the chart.",
        ApplyIntakeDemographicsRequestSchema.parse(input),
        signal,
      );
    },

    async applySchedulingIntakeChartEntries(
      appointmentId: string,
      input: ApplyIntakeChartEntriesRequest,
      signal?: AbortSignal,
    ): Promise<ApplyIntakeChartEntriesResponse> {
      return json(
        appointmentPath(appointmentId, "/intake/chart-entries"),
        ApplyIntakeChartEntriesResponseSchema,
        "The entries could not be added to the chart.",
        ApplyIntakeChartEntriesRequestSchema.parse(input),
        signal,
      );
    },

    async applySchedulingIntakeReason(
      appointmentId: string,
      input: ApplyIntakeReasonRequest,
      signal?: AbortSignal,
    ): Promise<ApplyIntakeReasonResponse> {
      return json(
        appointmentPath(appointmentId, "/intake/reason"),
        ApplyIntakeReasonResponseSchema,
        "The booking reason could not be updated.",
        ApplyIntakeReasonRequestSchema.parse(input),
        signal,
      );
    },

    /**
     * The probable charts for what the desk typed before adding a walk-in (PA-5): the same
     * matcher as the review (name and date of birth, date of birth and mobile number). A POST,
     * so no name or date of birth enters a URL; the read is audited without the typed values.
     */
    async getWalkInChartMatches(
      input: WalkInChartMatchesRequest,
      signal?: AbortSignal,
    ): Promise<SchedulingPatientMatches> {
      const matches = await json(
        "/v1/scheduling/walk-ins/chart-matches",
        SchedulingPatientMatchesSchema,
        "The check for an existing chart could not be completed.",
        WalkInChartMatchesRequestSchema.parse(input),
        signal,
      );
      if (matches.basis !== "walk_in") throw new Error("Unexpected chart match basis");
      return matches;
    },

    async markSchedulingIntakeSectionReviewed(
      appointmentId: string,
      input: MarkIntakeSectionReviewedRequest,
      signal?: AbortSignal,
    ): Promise<MarkIntakeSectionReviewedResponse> {
      return json(
        appointmentPath(appointmentId, "/intake/reviewed"),
        MarkIntakeSectionReviewedResponseSchema,
        "The section could not be marked reviewed.",
        MarkIntakeSectionReviewedRequestSchema.parse(input),
        signal,
      );
    },
  };
}

export type SchedulingIntakeClient = ReturnType<typeof createSchedulingIntakeClient>;

/**
 * A retry key for one apply: the same key is sent again only for a retry of the same click.
 * Within the contract's 8..100 characters, so the server's prefixes stay under 128.
 */
export function newIntakeIdempotencyKey(): string {
  return `intake-${crypto.randomUUID()}`;
}

/** The review has closed: read only from now on. */
export function isIntakeReviewClosed(error: unknown): boolean {
  return error instanceof SchedulingRequestError && error.detail === "intake_review_closed";
}

/** The probable charts changed since they were read: reload them before deciding. */
export function isChartMatchesChanged(error: unknown): boolean {
  return error instanceof SchedulingRequestError && error.detail === "chart_matches_changed";
}

/** The chart or the booking changed since it was read: reload before applying again. */
export function isIntakeTargetChanged(error: unknown): boolean {
  return (
    error instanceof SchedulingRequestError &&
    (error.detail === "chart_changed" || error.detail === "appointment_changed")
  );
}

/** The refusal's staff copy (the API's PHI-free text), or a fallback. */
export function intakeErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.length > 0 ? error.message : fallback;
}
